// The primary Investigations chat surface: ChatPanel (components/common/chat)
// bound to one investigation. This wrapper owns what is specific to a case:
// the API, where a citation opens (a call seeks in place or plays in the
// docked mini player; a file opens its viewer at the page; footage opens at
// the moment), the mini player inset, and the copy.
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlassIcon, PhoneIcon, ListBulletIcon, TableCellsIcon, DocumentTextIcon, UserIcon, FlagIcon, VideoCameraIcon } from "@heroicons/react/24/outline";
import { useStaffView } from "./StaffView";
import { useNowPlaying } from "./NowPlayingContext";
import { api, ChatCitation } from "./api";
import ChatPanel, { ChatClient, ChatCopy, IconComponent } from "../common/chat/ChatPanel";

interface Props {
  investigationId: string;
  investigationName?: string;
  // When provided (call detail), citations on this call seek in place
  // instead of navigating.
  onSeek?: (callId: string, ms: number) => boolean;
  // Compact = side-rail mode (call detail). Full = the main tab.
  compact?: boolean;
  // Compact mode: collapse the side panel.
  onClose?: () => void;
  // Opened from a call: start a fresh thread bound to it. The assistant
  // answers from that call first but can still use the rest of the case.
  focusCall?: { id: string; label: string };
}

const FOCUS_SUGGESTIONS = [
  { icon: ListBulletIcon, text: "Summarize this call in a few sentences." },
  { icon: FlagIcon, text: "What's flagged in this call, and is any of it new compared to the other calls?" },
  { icon: UserIcon, text: "Who is he talking to here, and how do they know each other?" },
  { icon: MagnifyingGlassIcon, text: "Any coded language or slang in this call? Give your best reading." },
  { icon: PhoneIcon, text: "Does anything said here contradict what he said on other calls?" },
  { icon: TableCellsIcon, text: "List every name, place, and amount of money mentioned in this call." },
];

const SUGGESTIONS = [
  { icon: FlagIcon, text: "Which calls should I listen to first, and why?" },
  { icon: UserIcon, text: "Who does he call most, and what do they talk about?" },
  { icon: MagnifyingGlassIcon, text: 'Any mention of a weapon, a gun, or "the thing"?' },
  { icon: PhoneIcon, text: "Has anyone been asked to contact a witness?" },
  { icon: TableCellsIcon, text: "Summarize every mention of money, CashApp, or bail." },
  { icon: ListBulletIcon, text: "Build a timeline of what he says happened that night." },
];

const TOOL_ICON: Record<string, IconComponent> = {
  search_transcripts: MagnifyingGlassIcon,
  get_call: PhoneIcon,
  list_calls: ListBulletIcon,
  get_leads: FlagIcon,
  get_entity: UserIcon,
  query_spreadsheet: TableCellsIcon,
  get_document_pages: DocumentTextIcon,
  get_video_events: VideoCameraIcon,
};

// "Aug 15 call" from a call label, else the file name.
const sourceLabel = (c: ChatCitation) => {
  const label = c.label;
  const m = label.match(/(\d{2})-(\d{2})-(\d{4})/);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} call`;
  }
  const iso = label.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} call`;
  }
  return label.replace(/ @ .*$/, "").slice(0, 28);
};

const InvestigationChat: React.FC<Props> = ({ investigationId, investigationName, onSeek, compact, onClose, focusCall }) => {
  const navigate = useNavigate();
  const staffView = Boolean(useStaffView());
  const { dockHeight, play } = useNowPlaying();

  const client = useMemo<ChatClient>(
    () => ({
      listThreads: () => api.threads(investigationId),
      createThread: ({ focusId }) => api.createThread(investigationId, focusId ? { focusCallId: focusId } : {}),
      messages: (threadId) => api.messages(investigationId, threadId),
      archiveThread: (threadId) => api.archiveThread(investigationId, threadId),
      streamUrl: (threadId) => api.chatUrl(investigationId, threadId),
    }),
    [investigationId]
  );

  // Call citations play in the docked mini player rather than leaving the
  // chat: on call detail the transcript seeks in place (onSeek); anywhere
  // else the mini player takes it. Files still open their viewer.
  const openCitation = (c: ChatCitation) => {
    if (c.kind === "video_segment" && c.fileId) {
      navigate(`/investigations/${investigationId}/files/${c.fileId}${c.startMs != null ? `?t=${c.startMs}` : ""}`);
      return;
    }
    if (c.callId) {
      if (onSeek && onSeek(c.callId, c.startMs || 0)) return;
      play(investigationId, c.callId, c.startMs || 0);
    } else if (c.fileId) {
      navigate(`/investigations/${investigationId}/files/${c.fileId}${c.page ? `?page=${c.page}` : ""}`);
    }
  };

  const name = investigationName || "this investigation";
  const copy: ChatCopy = {
    tagline: "Cited answers over every call, document, and spreadsheet in this investigation",
    placeholder: `Ask anything about ${name}…`,
    compactPlaceholder: "Ask about this investigation…",
    emptyTitle: `What do you want to know about ${name}?`,
    compactEmptyTitle: "Ask about this investigation",
    emptyBlurb: "Every answer is grounded in the transcripts and files, with a numbered citation you can click to hear the moment.",
    suggestions: SUGGESTIONS,
    focusPlaceholder: "Ask about this call…",
    focusEmptyTitle: "Ask about this call",
    focusSuggestions: FOCUS_SUGGESTIONS,
    focusNote: "still searches the whole case",
  };

  return (
    <ChatPanel
      client={client}
      onOpenCitation={openCitation}
      copy={copy}
      disclaimerKind="chat"
      railStorageKey="pnai-investigationChatRailWidth"
      toolIcons={TOOL_ICON}
      avatarIcon={MagnifyingGlassIcon}
      compact={compact}
      onClose={onClose}
      focus={focusCall ? { id: focusCall.id, label: focusCall.label, icon: PhoneIcon } : undefined}
      bottomInset={dockHeight}
      readOnlyNotice={staffView ? "Chat is off in staff view. Officers' own chat threads stay private to them." : null}
      labelFor={sourceLabel}
      sourceIcon={(c) => (c.callId ? PhoneIcon : DocumentTextIcon)}
    />
  );
};

export default InvestigationChat;
